import React, { useRef, useState } from 'react';
import Slider from 'react-slick';
import { StaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';

import H2 from '@rotaready/frecl/build/H2';
import H3 from '@rotaready/frecl/build/H3';
import Text from '@rotaready/frecl/build/Text';

import { ViewportBreakpoints } from '../../cssConstants';

import TextHeadingWrapper from '../../layout/TextHeadingWrapper';
import ResponsiveContainer from '../../layout/ResponsiveContainer';

const CaseStudies = styled.div`
  padding: 40px 0;
  position: relative;
  overflow: visible;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    padding: 85px 0;
  }
`;

const DiagonalStripes = styled.div`
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 0;
  display: none;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    display: block;
  }
`;

const DiagonalStripe = styled.span`
  display: inline-block;
  position: absolute;
  box-shadow: 0 0 100px 0 rgba(71,91,104,0.2);
  width: 250%;
`;

const DarkStripe = styled(DiagonalStripe)`
  background-color: rgba(13, 81, 255, 0.5);
  transform: rotate(60deg);
  left: -400px;
  height: 150px;
`;

const LightStripe = styled(DiagonalStripe)`
  background-color: ${({ theme: { colors } }) => colors.grey20};
  transform: rotate(30deg);
  right: 300px;
  height: 350px;
`;

const ShadowBox = styled.div`
  text-align: center;
  box-shadow: 0 0 8px 2px ${({ theme: { colors } }) => colors.grey20};
  border: solid 1px ${({ theme: { colors } }) => colors.grey20};
  background: ${({ theme: { colors } }) => colors.white};
  padding: 40px;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    padding: 50px 100px;
  }
`;

const CaseStudiesInnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const CaseStudiesContentArea = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    width: 60%;
  }

  // This is necessary to vertically centre the content of each slide
  .slick-initialized .slick-track {
    display: flex;
    align-items: center;
  }
`;

const LogoBar = styled.div`
  display: flex;
  flex-direction: row;
`;

const activeLogo = () => css`
  border-bottom: 2px solid ${({ theme: { colors } }) => colors.brand};
  opacity: 1;
`;

const Logo = styled.div`
  flex: 1 1 33%;
  text-align: center;
  padding: 20px;
  opacity: 0.5;
  cursor: pointer;

  ${({ active }) => active && activeLogo}

  &:hover {
    opacity: 1;
  }

  > div {
    margin: 0 auto;
  }
`;

const Quote = styled(H3).attrs({
  uistyle: 'brand160',
})`
  margin-bottom: 40px;
`;

const Attribution = styled(Text).attrs({
  size: 'lg',
})``;

const Slide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CaseStudyCarousel = () => {
  const slider = useRef(null);
  const [currentIndex, setIndex] = useState(0);

  const goTo = index => () => {
    slider.current.slickGoTo(index);
    setIndex(index);
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    fade: true,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 3000,
  };

  return (
    <StaticQuery
      query={graphql`
        query {
          brewdogLogo: file(relativePath: { eq: "client-logos/brewdog.png" }) {
            childImageSharp {
              gatsbyImageData(quality:100, width:39)
            }
          }

          warnerLogo: file(relativePath: { eq: "client-logos/warner-leisure-hotels.jpg" }) {
            childImageSharp {
              gatsbyImageData(quality:100, width:99)
            }
          }

          stormfrontLogo: file(relativePath: { eq: "client-logos/stormfront.png" }) {
            childImageSharp {
              gatsbyImageData(quality:100, height:40)
            }
          }
        }
      `}
      render={data => (
        <ResponsiveContainer>
          <CaseStudies>
            <DiagonalStripes>
              <LightStripe />
              <DarkStripe />
            </DiagonalStripes>

            <TextHeadingWrapper>
              <H2 text="See what our customers say" uistyle="brand160" />
            </TextHeadingWrapper>

            <CaseStudiesInnerWrapper>
              <CaseStudiesContentArea>
                <LogoBar>
                  <Logo onClick={goTo(0)} active={currentIndex === 0}>
                    <GatsbyImage image={data.brewdogLogo.childImageSharp.gatsbyImageData} alt="BrewDog" />
                  </Logo>
                  <Logo onClick={goTo(1)} active={currentIndex === 1}>
                    <GatsbyImage image={data.warnerLogo.childImageSharp.gatsbyImageData} alt="Warner Leisure Hotels" />
                  </Logo>
                  <Logo onClick={goTo(2)} active={currentIndex === 2}>
                    <GatsbyImage image={data.stormfrontLogo.childImageSharp.gatsbyImageData} alt="Stormfront" />
                  </Logo>
                </LogoBar>
                <ShadowBox>
                  <Slider ref={slider} {...settings}>
                    <Slide>
                      <Quote text="With Rotaready we're reaping huge benefits on staff engagement and service quality improvements. We're delighted to work with a company committed to continual improvement through dialogue with its customers." />
                      <Attribution text="BrewDog - James Brown" />
                    </Slide>
                    <Slide>
                      <Quote text="Rotaready is that rare thing: a tech company that has kept its promises and made an immediate positive impact." />
                      <Attribution text="Warner Leisure Hotels - James Johnson" />
                    </Slide>
                    <Slide>
                      <Quote text="Rotaready makes the invisible visible, allowing me to run the business more efficiently and use our staff in a more effective way. I am delighted with the software and the people are first class." />
                      <Attribution text="Stormfront (Apple Retail) - Daniel Evans" />
                    </Slide>
                  </Slider>
                </ShadowBox>
              </CaseStudiesContentArea>
            </CaseStudiesInnerWrapper>
          </CaseStudies>
        </ResponsiveContainer>
      )}
    />
  );
};

export default CaseStudyCarousel;
